'use client';

import { AssetPath } from '@gnsx/genesys.js';

import { parseBuildStampBuiltAt } from './buildStampParse';

import { editorProject } from '@/editor/store/editorStore';
import { IS_DEV } from '@/lib/utils';

export { parseBuildStampBuiltAt } from './buildStampParse';

export const BUILD_STAMP_ASSET_PATH = '@project/.editor/build-stamp.json';

/** `builtAt` from the last build refresh the editor applied (in-app or external). */
let lastAppliedBuiltAt: string | null = null;

/** True when the stamp matches a build the editor has already refreshed for. */
export function isBuildStampAlreadyApplied(builtAt: string | null): boolean {
  if (lastAppliedBuiltAt == null || builtAt == null) {
    return false;
  }
  return lastAppliedBuiltAt === builtAt;
}

/** Read the current build stamp from disk. Returns null when missing or unreadable. */
export async function readCurrentBuildStampBuiltAt(): Promise<string | null> {
  if (!editorProject.isProjectOpened) {
    return null;
  }
  try {
    const storageProvider = editorProject.makeScopedStorageProvider();
    const stampPath = AssetPath.fromString(BUILD_STAMP_ASSET_PATH);
    const exists = await storageProvider.exists(stampPath, { silent: true });
    if (!exists) {
      return null;
    }
    const content = await storageProvider.downloadFile(stampPath);
    if (typeof content !== 'string') {
      return null;
    }
    return parseBuildStampBuiltAt(content);
  } catch {
    return null;
  }
}

/** Record the on-disk stamp after a successful `refreshAfterBuild`. */
export async function captureAppliedBuildStampFromDisk(): Promise<void> {
  lastAppliedBuiltAt = await readCurrentBuildStampBuiltAt();
  if (IS_DEV && lastAppliedBuiltAt != null) {
    console.debug('[BuildStampState] Captured applied build stamp:', lastAppliedBuiltAt);
  }
}

/** Clear tracked stamp when the project file watcher is torn down. */
export function resetAppliedBuildStamp(): void {
  lastAppliedBuiltAt = null;
}
